import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subscription } from 'rxjs';
import { PUITranslatePipe } from '../pipes/p-ui-translate.pipe';

@Component({
    selector: 'app-session-timeout-dialog',
    standalone: true,
    imports: [ButtonModule, PUITranslatePipe],
    template: `
        <div class="surface-card border-round-md z-5">
            <div class="px-3">
                <!-- Image Section -->
                <div class="flex justify-content-center mb-3">
                    <img class="w-26rem" [src]="config.data.imagePath" />
                </div>

                <!-- Timer Section - Only show when not expired -->
                @if (!config.data.isSessionExpired) {
                    <div class="flex justify-content-center h-4rem py-2 px-4">
                        <div
                            class="flex align-items-center justify-content-center px-3 py-2 border-round border-1 mr-2 h-full surface-100 surface-border"
                        >
                            <i class="pi pi-clock text-gray-300"></i>
                        </div>
                        <div
                            class="flex align-items-center text-color px-3 py-2 border-round border-1 font-bold text-lg surface-100 surface-border mr-2 h-full"
                        >
                            {{ minutes }} <span class="m-1">min</span>
                        </div>
                        <span class="font-bold text-color text-lg flex align-items-center mx-1">:</span>
                        <div
                            class="flex align-items-center text-color px-3 py-2 border-round border-1 font-bold text-lg surface-100 surface-border ml-2 h-full"
                        >
                            {{ seconds }} <span class="m-1">sec</span>
                        </div>
                    </div>
                }

                <p class="text-center text-color font-semibold mb-2">
                    {{
                        (config.data.isSessionExpired
                            ? 'default-platform-sessionExpired'
                            : 'default-platform-stillWithUs'
                        ) | pTranslate: (config.data.isSessionExpired ? 'Your session has expired' : 'Still with us?')
                    }}
                </p>

                <p class="text-center text-sm text-gray-300">
                    {{
                        (config.data.isSessionExpired
                            ? 'default-platform-redirectMessage'
                            : 'default-platform-sessionSecurityMessage'
                        )
                            | pTranslate
                                : (config.data.isSessionExpired
                                      ? 'You will be redirected to the Login page'
                                      : 'For your security, we log you out automatically when you have not been active for a certain period of time')
                    }}
                </p>
            </div>

            <div class="flex gap-16 pt-4 px-2 pb-2">
                @if (!config.data.isSessionExpired) {
                    <p-button
                        label="{{ 'default-cm-logout' | pTranslate: 'Log out' }}"
                        [outlined]="true"
                        class="w-full"
                        styleClass="w-full"
                        (onClick)="onLogout()"
                    ></p-button>
                }
                <p-button
                    label="{{
                        (config.data.isSessionExpired ? 'default-platform-okay' : 'default-platform-continueSession')
                            | pTranslate: (config.data.isSessionExpired ? 'Okay' : 'Continue session')
                    }}"
                    class="w-full"
                    styleClass="w-full"
                    (onClick)="config.data.isSessionExpired ? onLogIn() : onContinue()"
                ></p-button>
            </div>
        </div>
    `,
})
export class SessionTimeoutDialogComponent implements OnInit {
    remainingTime$!: Subscription;
    minutes: number = 0;
    seconds: number = 0;

    private destroyRef = inject(DestroyRef);
    private ref = inject(DynamicDialogRef);
    public config = inject(DynamicDialogConfig);

    ngOnInit() {
        if (this.config.data.timeRemaining) {
            this.remainingTime$ = this.config.data.timeRemaining
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((time: number) => {
                    this.minutes = Math.floor(time / 60);
                    this.seconds = time % 60;
                });
        }
    }

    onContinue(): void {
        this.ref.close('continue');
    }

    onLogout(): void {
        this.ref.close('logout');
    }

    onLogIn(): void {
        this.ref.close('login');
    }
}
